import merge from "lodash.merge";
import { CSSObject } from "styled-components";
import { resolvePaletteRoleForegroundToken } from "../../../../../../functions/resolvePaletteRoleForegroundToken";
import {
	ColorPaletteRoleBackgroundOptions,
	Theme,
} from "../../../../../../types/theming";

export const LIST_ITEM_EASE_IN_DURATION = 300;
export const LIST_ITEM_EASE_OUT_DURATION = 500;

const cssObjectForListItemColorStates = (
	theme: Theme,
	colorOn: ColorPaletteRoleBackgroundOptions | undefined
): CSSObject => {
	const COLOR_PRIMARY_ACCENT =
		theme.palette.hexForMandatory("primary_accent_200");
	const COLOR_CTA = theme.palette.hexForRole("cta_active");
	const COLOR_FG_LIGHT = theme.palette.hexForRole("fg_light_1");
	const COLOR_FG_DARK = theme.palette.hexForRole("fg_dark_1");
	const COLOR_FG = theme.palette.hexForRole(
		resolvePaletteRoleForegroundToken(colorOn)
	);

	const commonHoverStateObj: CSSObject = {
		"&:hover": {
			cursor: "pointer",
			backgroundColor: COLOR_PRIMARY_ACCENT,
		},

		"&.ListItem--focussed": {
			backgroundColor: COLOR_PRIMARY_ACCENT,
		},
	};

	const commonActiveStateObj: CSSObject = {
		"&:active": {
			backgroundColor: COLOR_CTA,
			color: COLOR_FG_LIGHT,
			".Typography": {
				color: COLOR_FG_LIGHT,
			},
			".Icon svg": {
				fill: COLOR_FG_LIGHT,
			},
		},
	};

	const darkOnlyHoverStateObj: CSSObject = {
		"&:hover, &.ListItem--focussed": {
			color: COLOR_FG_DARK,
			".Typography": {
				color: COLOR_FG_DARK,
			},
			".Icon svg": {
				fill: COLOR_FG_DARK,
			},
		},
	};

	const getInteractiveStates = () => {
		switch (colorOn) {
			case "bg_dark":
			case "bg_dark_variant": {
				// It's important that commonActiveStateObj follows darkOnlyHoverStateObj in merging...
				return merge(
					commonHoverStateObj,
					darkOnlyHoverStateObj,
					commonActiveStateObj
				);
			}
			default: {
				return merge(commonHoverStateObj, commonActiveStateObj);
			}
		}
	};

	return {
		color: COLOR_FG,

		".Typography": {
			color: COLOR_FG,
			transitionProperty: "color",
		},

		".Icon svg": {
			fill: COLOR_FG,
			transitionProperty: "fill",
		},

		transitionProperty: "color, background-color",

		"&:hover, &:hover .Typography, &:hover .Icon svg": {
			transitionDuration: `${LIST_ITEM_EASE_OUT_DURATION}ms`,
			transitionTimingFunction: "ease-out",
		},

		"&:active, &:active .Typography, &:active .Icon svg": {
			transitionDuration: `${LIST_ITEM_EASE_IN_DURATION}ms`,
			transitionTimingFunction: "ease-in",
		},

		":not(&.ListItem--readonly, &.ListItem--disabled)": getInteractiveStates(),
	};
};

export default cssObjectForListItemColorStates;
